import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LABELS = {
  "purchase-req-create": "Purchase Requisition Create",
  "purchase-order-create": "Purchase Order Create",
  "move-order-create": "Move Order Creation",
  "transact-move-order":"Transact Move Order",
  "rfp-create": "RFP Create",
  "manufacturing-work-order":"Manufacturing WorkOrder",
  "maintenance-work-order":"Maintenance WorkOrder",
  "create-hr": "HR Worker Manager",
};

const HINTS = {
  "invoice-create": 'e.g. "Create AP invoice for supplier ABC Ltd, $5000, NET30"',
  "supplier-create": 'e.g. "Create supplier Acme Inc, USA, payment terms NET45"',
  "invoice-ageing": 'e.g. "Show invoices overdue more than 30 days"',
  "supplier-ageing": 'e.g. "List top 10 suppliers by outstanding balance"',
  "journal-create": 'e.g. "Post journal: Debit Cash $1000, Credit Sales $1000"',
  "receipt-create": 'e.g. "Apply $500 receipt to customer XYZ invoice INV-001"',
  "customer-create": 'e.g. "Create customer Globex Corp, billing USD, NET30"',
  "get-hr": 'e.g. "List active employees in Finance department"',
  "create-hr": 'e.g. "Create employee John Doe, Accountant, joining 2026-07-01"',
  "employee-leave-application": 'e.g. "Apply annual leave from 9 to 10 November"',
  "purchase-req-create": 'e.g. "Raise a requisition for 10 laptops, $1200 each, deliver to HQ by next month"',
  "purchase-order-create": 'e.g. "Create a PO to supplier Acme for 100 units of item A at $25, ship to Dubai warehouse"',
  "rfp-create": 'e.g. "Create an RFP for office furniture, invite suppliers Acme and Globex, close in 14 days"',
  "bank-transfer": 'e.g. "Transfer $50,000 from Corporate USD account to Operations USD account today"',
  "bank-balance": 'e.g. "Show the latest balance of the Corporate USD bank account"',
  "asset-addition": 'e.g. "Add a new laptop asset, cost $1500, in service today, category Computer-PC, location San Jose"',
  "asset-transfer": 'e.g. "Transfer asset FA-1001 from San Jose to Dubai office"',
  "asset-retirement": 'e.g. "Retire asset FA-1001 and dispose of it"',
  "sales-order-booking": 'e.g. "Book a sales order for ABC Corp: 10 units of AS54888 at $250, ship 2026-05-01, book immediately"',
  "finance-account-overview": 'e.g. "Show Apr-26 trial balance for Vision Operations ledger — opening, activity, closing per account"',
  "finance-anomaly": 'e.g. "Find unusual journals in Apr-26 over $100K and drill to AP / AR root causes"',
  "finance-variance": 'e.g. "Compare Apr-26 actuals vs budget for Vision Operations and flag variances over 10%"',
  "finance-future-insight": 'e.g. "Forecast Q3-26 revenue accounts (4xxx) based on the last 6 months of activity"',
  "finance-narrative": 'e.g. "Generate Apr-26 MIS commentary for Vision Operations and email it to cfo@example.com"',
  "finance-monthend": 'e.g. "Check Apr-26 close readiness for Vision Operations across GL, AP, AR, FA"',
  "move-order-create": 'e.g. "Create a move order for item from one to another subinventory"',
  "transact-move-order": 'e.g. "Create a Transact move order for the movement request 12345"',
  "manufacturing-work-order":'e.g. "Create a Workorder for the Item 1200-00-000"',
  "maintenance-work-order":'e.g. "Create a Workorder for the AssetNumber 12345"',
  "item-creation": 'e.g. "Create New item with item number 12314-4124-444 and description of Motor-3HP"',
};

const ErpContext = createContext(null);

export function ErpProvider({ children }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [agents, setAgents] = useState([]);
  const [openAgent, setOpenAgent] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [pendingAgent, setPendingAgent] = useState(null);

  // Calls /erp-agents/my-profile and derives the `configured` flag from the response.
  // Falls back to { configured: false } on 404 (no profile saved yet) or any error.
  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/erp-agents/my-profile");
      const enriched = {
        ...data,
        configured: !!(data.oracle_username && data.business_unit),
      };
      setProfile(enriched);
      return enriched;
    } catch {
      setProfile({ configured: false });
      return { configured: false };
    }
  }, []);

  const fetchCatalog = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const assignedAgents = data.agents || [];
      const flat = assignedAgents.map((agentKey) => ({
        key: agentKey,
        label: LABELS[agentKey] || agentKey
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
        category: agentKey.startsWith("finance-") ? "finance_efficiency" : "core",
        hint: HINTS[agentKey] || "",
        is_custom: false,
      }));
      setAgents(flat);
      return flat;
    } catch (err) {
      console.error("Agent catalog error:", err);
      setAgents([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchCatalog();
    } else {
      setProfile(null);
      setAgents([]);
    }
  }, [user, fetchProfile, fetchCatalog]);

  const launchAgent = useCallback(
    (key) => {
      if (profile && profile.configured) {
        setOpenAgent(key);
      } else {
        setPendingAgent(key);
        setSetupOpen(true);
      }
    },
    [profile]
  );

  const onProfileSaved = useCallback(async () => {
    setSetupOpen(false);
    const fresh = await fetchProfile();
    if (pendingAgent && fresh.configured) {
      setOpenAgent(pendingAgent);
      setPendingAgent(null);
    }
  }, [pendingAgent, fetchProfile]);

  const value = {
    profile, fetchProfile,
    agents, fetchCatalog,
    openAgent, setOpenAgent,
    setupOpen, setSetupOpen,
    pendingAgent, setPendingAgent,
    launchAgent, onProfileSaved,
  };

  return <ErpContext.Provider value={value}>{children}</ErpContext.Provider>;
}

export const useErp = () => useContext(ErpContext);

// Back-compat: a static AGENTS list still consumed by DashboardPage for label lookup.
// New code should read from useErp().agents instead.
export const AGENTS = Object.entries(HINTS).map(([key, hint]) => ({
  key,
  hint,
  category: key.startsWith("finance-") ? "finance_efficiency" : "core",
  label: LABELS[key] || key.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" "),
}));